import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { API_BASE_URL } from "../../environment";
import { formatCustomDateTime } from "../../utils/dateUtils";
import { showErrorAlert, showSuccessAlert } from "../../utils/alertUtils";
import { AuthContext } from "../../context/authcontext";
import male from "../../assets/male.png";
import { getLifecycleTypeName } from "../../utils/lifecycleUtils";
import { useTabVisibility } from "../../hooks/useTabVisibility";
import { getStageColor } from "../../utils/stageColors";
import Tooltip from "@mui/material/Tooltip";

interface Lead {
  lead_id: number;
  created_by_username: string;
  updated_by_username: string;
  title: string;
  source: string;
  score: number;
  preferred_language: string;
  time_zone: string;
  created_datetime: string;
  updated_datetime: string;
  created_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  updated_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  assigned_datetime: string;
  estimated_value: number;
  currency_code: string;
  priority: string;
  products_n_services: string | null;
  primary_contact: {
    contact_id: number;
    primary_email: string;
    personal_email: string;
    primary_phone: string;
    mobile_phone: string;
    preferred_language: string;
    address_line1: string;
    address_line2: string;
    source: string;
    city: string;
    state: string;
    country: string;
    full_name: string;
    avatar_url: string;
    job_title: string;
  };
  company: {
    company_id: number;
    company_name: string;
    business_type_display_name: string;
    industry_type_display_name: string;
    avatar_url: string;
    address: string | null;
  };
  lifecycle_type_stage: {
    lifecycle_type_stage_id: number;
    stage_name: string;
    stage_order?: number;
  };
  assigned_user: {
    user_id: number;
    full_name: string;
    email: string;
    phone: string | null;
    avatar_url: string;
  };
  tenant: {
    tenant_id: number;
    tenant_name: string;
    domain_name: string;
    is_active: boolean;
    language: string | null;
  };
  assigned_team: {
    team_id: number;
    team_name: string;
    created_datetime: string;
  };
  lifecycle_source: {
    lifecycle_source_id: number;
    lifecycle_source_key: string;
    lifecycle_source_display_name: string;
    source_order?: number;
  };
}

interface Pipeline {
  pipeline_id: number;
  created_by_username: string;
  updated_by_username: string;
  title: string;
  created_datetime: string;
  updated_datetime: string;
  assigned_datetime: string;
  priority: string;
  products_n_services: string | null;
  primary_contact: {
    contact_id: number;
    primary_email: string;
    personal_email: string;
    primary_phone: string;
    mobile_phone: string;
    preferred_language: string;
    address_line1: string;
    address_line2: string;
    source: string;
    city: string;
    state: string;
    country: string;
    full_name: string;
    avatar_url: string;
    job_title: string;
  };
  created_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  updated_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  assigned_team_id: number;
  company: {
    company_id: number;
    company_name: string;
    business_type_display_name: string;
    industry_type_display_name: string;
    avatar_url: string;
    address: string | null;
  };
  lifecycle_type_stage: {
    lifecycle_type_stage_id: number;
    stage_name: string;
    stage_order: number;
  };
  assigned_user: {
    user_id: number;
    full_name: string;
    email: string;
    phone: string | null;
    avatar_url: string;
  };
  tenant: {
    tenant_id: number;
    tenant_name: string;
    domain_name: string;
    is_active: boolean;
    language: string | null;
  };
  realted_lead: {
    lead_id: number;
    title: string;
    created_datetime: string;
    updated_datetime: string;
    source: string;
    priority: string;
  };
  assigned_team: {
    team_id: number;
    team_name: string;
    created_datetime: string;
  };
}

interface Deal {
  deal_id: number;
  created_by_username: string;
  updated_by_username: string;
  title: string;
  created_datetime: string;
  updated_datetime: string;
  assigned_datetime: string;
  estimated_value: number | null;
  currency_code: string;
  priority: string;
  products_n_services: string | null;
  primary_contact: {
    contact_id: number;
    primary_email: string;
    personal_email: string;
    primary_phone: string;
    mobile_phone: string;
    preferred_language: string;
    address_line1: string;
    address_line2: string;
    source: string;
    city: string;
    state: string;
    country: string;
    full_name: string;
    avatar_url: string;
    job_title: string;
  };
  created_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  updated_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  company: {
    company_id: number;
    company_name: string;
    business_type_display_name: string;
    industry_type_display_name: string;
    avatar_url: string;
    address: string | null;
  };
  lifecycle_type_stage: {
    lifecycle_type_stage_id: number;
    stage_name: string;
    stage_order?: number;
  };
  assigned_user: {
    user_id: number;
    full_name: string;
    email: string;
    phone: string | null;
    avatar_url: string;
  };
  tenant: {
    tenant_id: number;
    tenant_name: string;
    domain_name: string;
    is_active: boolean;
    language: string | null;
  };
  realted_lead: {
    lead_id: number;
    title: string;
    created_datetime: string;
    updated_datetime: string;
    source: string;
    priority: string;
  };
  related_pipeline: {
    pipeline_id: number;
    title: string;
    created_datetime: string;
    updated_datetime: string;
    priority: string;
  };
  assigned_team: {
    team_id: number;
    team_name: string;
    created_datetime: string;
  };
  assigned_team_id?: number;
}

interface StagesProps {
  tenant_id?: number | string | null;
  related_entity_id?: number | string | null;
  refreshTrigger?: number;
  onActivityUpdate?: () => void;
  contactData?: any;
}

const stageTypeOptions = [
  { value: "lead", label: getLifecycleTypeName("LEAD") },
  { value: "pipeline", label: getLifecycleTypeName("PIPELINE") },
  { value: "deal", label: getLifecycleTypeName("DEAL") },
];

const Stages: React.FC<StagesProps> = ({
  tenant_id,
  related_entity_id,
  refreshTrigger,
  onActivityUpdate,
  contactData,
}) => {
  const { auth } = useContext(AuthContext);
  const [selectedStageType, setSelectedStageType] = useState(
    stageTypeOptions[0]
  );
  const [leads, setLeads] = useState<Lead[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [creatingEntity, setCreatingEntity] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const currentTenantId = Number(
    tenant_id || localStorage.getItem("selectedTenantId")
  );
  const currentContactId = Number(related_entity_id);
  const authHeader = localStorage.getItem("authCred");

  const { isVisible, hasBeenActivated } = useTabVisibility({
    tabId: "stages",
    onTabActivated: () => {
      if (!dataLoaded && currentTenantId && currentContactId) {
        loadStageData();
        setDataLoaded(true);
      }
    },
  });

  const getInitialStageId = async (
    entityType: string
  ): Promise<number | null> => {
    try {
      if (!authHeader) {
        throw new Error("Authentication required");
      }

      const lifecycleTypesResponse = await fetch(
        `${API_BASE_URL}/lifecycle-type/?tenant_id=${currentTenantId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
        }
      );

      if (!lifecycleTypesResponse.ok) {
        throw new Error("Failed to fetch lifecycle types");
      }

      const lifecycleTypesData = await lifecycleTypesResponse.json();
      const lifecycleTypes = lifecycleTypesData.results || lifecycleTypesData;

      const lifecycleTypeKey = entityType.toUpperCase();
      const lifecycleType = lifecycleTypes.find(
        (type: any) => type.lifecycle_type_key === lifecycleTypeKey
      );

      if (!lifecycleType) {
        console.warn(`No lifecycle type found for ${entityType}`);
        return null;
      }

      const stagesResponse = await fetch(
        `${API_BASE_URL}/batch/lifecycle-type-stage/?lifecycle_type_id=${lifecycleType.lifecycle_type_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
        }
      );

      if (!stagesResponse.ok) {
        throw new Error("Failed to fetch lifecycle stages");
      }

      const stagesData = await stagesResponse.json();
      const stages = Array.isArray(stagesData) ? stagesData : [];

      const initialStage = stages.find((stage: any) => stage.stage_order === 1);

      if (!initialStage) {
        console.warn(`No initial stage found for ${entityType}`);
        return null;
      }

      return initialStage.lifecycle_type_stage_id;
    } catch (error) {
      console.error(`Error fetching initial stage for ${entityType}:`, error);
      return null;
    }
  };

  useEffect(() => {
    if (
      hasBeenActivated &&
      !dataLoaded &&
      currentTenantId &&
      currentContactId
    ) {
      loadStageData();
      setDataLoaded(true);
    }
  }, [hasBeenActivated, dataLoaded, currentTenantId, currentContactId]);

  useEffect(() => {
    if (dataLoaded && refreshTrigger && currentTenantId && currentContactId) {
      loadStageData();
    }
  }, [refreshTrigger, dataLoaded, currentTenantId, currentContactId]);

  useEffect(() => {
    if (hasBeenActivated && currentTenantId && currentContactId) {
      loadStageData();
    }
  }, [
    selectedStageType.value,
    hasBeenActivated,
    currentTenantId,
    currentContactId,
  ]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedItems([]);
    setSelectAll(false);
  }, [selectedStageType.value]);

  useEffect(() => {
    setSelectedItems([]);
    setSelectAll(false);
  }, [currentPage]);

  const loadStageData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!authHeader) {
        throw new Error("Authentication required");
      }

      const endpoint = `${API_BASE_URL}/${selectedStageType.value}/?tenant_id=${currentTenantId}&contact_id=${currentContactId}`;

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${authHeader}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch ${selectedStageType.label.toLowerCase()} data`
        );
      }

      const data = await response.json();
      const results = data.results || [];

      switch (selectedStageType.value) {
        case "lead":
          setLeads(results);
          break;
        case "pipeline":
          setPipelines(results);
          break;
        case "deal":
          setDeals(results);
          break;
        default:
          break;
      }
    } catch (error: any) {
      console.error(
        `Error loading ${selectedStageType.label.toLowerCase()} data:`,
        error
      );
      setError(
        error.message ||
        `Failed to load ${selectedStageType.label.toLowerCase()} data`
      );
      showErrorAlert(
        error.message ||
        `Failed to load ${selectedStageType.label.toLowerCase()} data`
      );
    } finally {
      setLoading(false);
    }
  };

  const getCurrentData = () => {
    switch (selectedStageType.value) {
      case "lead":
        return leads;
      case "pipeline":
        return pipelines;
      case "deal":
        return deals;
      default:
        return [];
    }
  };

  const allData = getCurrentData();
  const totalPages = Math.ceil(allData.length / rowsPerPage);
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentData = allData.slice(indexOfFirst, indexOfLast);

  const getPageNumbers = () => {
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return (
          <i className="ti ti-square-rounded-filled me-1 text-danger circle" />
        );
      case "medium":
        return (
          <i className="ti ti-square-rounded-filled me-1 text-secondary circle" />
        );
      case "low":
        return (
          <i className="ti ti-square-rounded-filled me-1 text-info circle" />
        );
      default:
        return (
          <i className="ti ti-square-rounded-filled me-1 text-secondary circle" />
        );
    }
  };

  const formatDateTime = (dateString: string) => {
    return formatCustomDateTime(dateString);
  };

  const getUserAvatar = (user: any) => {
    return user?.avatar_url || male;
  };

  const createLead = async () => {
    if (creatingEntity) return;

    setCreatingEntity(true);
    try {
      const initialStageId = await getInitialStageId("LEAD");

      const validLifecycleSourceId = contactData?.lifecycleSource?.lifecycle_source_id || contactData?.lifecycle_source_id;

      const payload: any = {
        title: contactData?.name || "New Lead",
        preferred_language: contactData?.preferredLanguage || "",
        primary_contact: currentContactId,
        company_id: contactData?.companyId || null,
        assigned_user_id: contactData?.assignedUser?.user_id || auth?.user_id,
        assigned_team_id: contactData?.assignedTeam?.team_id || null,
        tenant_id: currentTenantId,
        priority: "Medium",
        estimated_value: 0,
        currency_code: contactData?.currencyCode || "USD",
        created_by: auth?.user_id || null,
        lifecycle_type_stage_id: initialStageId,
      };

      if (validLifecycleSourceId) {
        payload.lifecycle_source_id = validLifecycleSourceId;
      } else {
        payload.source = contactData?.source || "Contact";
      }

      const response = await fetch(`${API_BASE_URL}/lead/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${authHeader}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to create lead");
      }

      await loadStageData();
      showSuccessAlert(
        `${getLifecycleTypeName("LEAD")} "${payload.title
        }" created successfully!`
      );

      if (onActivityUpdate) {
        onActivityUpdate();
      }
    } catch (error: any) {
      console.error("Error creating lead:", error);
      showErrorAlert(error.message || "Failed to create lead");
    } finally {
      setCreatingEntity(false);
    }
  };

  const createPipeline = async () => {
    if (creatingEntity) return;

    setCreatingEntity(true);
    try {
      const initialStageId = await getInitialStageId("PIPELINE");

      const validLifecycleSourceId = contactData?.lifecycleSource?.lifecycle_source_id || contactData?.lifecycle_source_id;

      const payload: any = {
        title: contactData?.name || "New Pipeline",
        preferred_language: contactData?.preferredLanguage || "",
        primary_contact: currentContactId,
        company_id: contactData?.companyId || null,
        assigned_user_id: contactData?.assignedUser?.user_id || auth?.user_id,
        assigned_team_id: contactData?.assignedTeam?.team_id || null,
        tenant_id: currentTenantId,
        priority: "Medium",
        currency_code: contactData?.currencyCode || "USD",
        realted_lead_id: leads.length > 0 ? leads[0].lead_id : null,
        created_by: auth?.user_id || null,
        lifecycle_type_stage_id: initialStageId,
      };

      if (validLifecycleSourceId) {
        payload.lifecycle_source_id = validLifecycleSourceId;
      } else {
        payload.source = contactData?.source || "Contact";
      }

      const response = await fetch(`${API_BASE_URL}/pipeline/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${authHeader}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to create pipeline");
      }

      await loadStageData();
      showSuccessAlert(`Pipeline "${payload.title}" created successfully!`);

      if (onActivityUpdate) {
        onActivityUpdate();
      }
    } catch (error: any) {
      console.error("Error creating pipeline:", error);
      showErrorAlert(error.message || "Failed to create pipeline");
    } finally {
      setCreatingEntity(false);
    }
  };

  const createDeal = async () => {
    if (creatingEntity) return;

    setCreatingEntity(true);
    try {
      const initialStageId = await getInitialStageId("DEAL");

      const validLifecycleSourceId = contactData?.lifecycleSource?.lifecycle_source_id || contactData?.lifecycle_source_id;

      const payload: any = {
        title: contactData?.name || "New Deal",
        preferred_language: contactData?.preferredLanguage || "",
        primary_contact: currentContactId,
        company_id: contactData?.companyId || null,
        assigned_user_id: contactData?.assignedUser?.user_id || auth?.user_id,
        assigned_team_id: contactData?.assignedTeam?.team_id || null,
        tenant_id: currentTenantId,
        priority: "Medium",
        currency_code: contactData?.currencyCode || "USD",
        exepected_amount: 0,
        realted_lead_id: leads.length > 0 ? leads[0].lead_id : null,
        related_pipeline_id:
          pipelines.length > 0 ? pipelines[0].pipeline_id : null,
        created_by: auth?.user_id || null,
        lifecycle_type_stage_id: initialStageId,
      };

      if (validLifecycleSourceId) {
        payload.lifecycle_source_id = validLifecycleSourceId;
      } else {
        payload.source = contactData?.source || "Contact";
      }

      const response = await fetch(`${API_BASE_URL}/deal/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${authHeader}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to create deal");
      }

      await loadStageData();
      showSuccessAlert(`Deal "${payload.title}" created successfully!`);

      if (onActivityUpdate) {
        onActivityUpdate();
      }
    } catch (error: any) {
      console.error("Error creating deal:", error);
      showErrorAlert(error.message || "Failed to create deal");
    } finally {
      setCreatingEntity(false);
    }
  };

  const handleAddNew = async () => {
    switch (selectedStageType.value) {
      case "lead":
        await createLead();
        break;
      case "pipeline":
        await createPipeline();
        break;
      case "deal":
        await createDeal();
        break;
      default:
        break;
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const validIds = (currentData as any[])
        .filter((item: any) => item.lead_id || item.pipeline_id || item.deal_id)
        .map(
          (item: any) =>
            (item.lead_id || item.pipeline_id || item.deal_id) as number
        );
      setSelectedItems(validIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId: number) => {
    setSelectedItems((prev) => {
      const newSelected = prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId];

      const validCurrentData = (currentData as any[]).filter(
        (item: any) => item.lead_id || item.pipeline_id || item.deal_id
      );
      setSelectAll(
        newSelected.length === validCurrentData.length &&
        validCurrentData.length > 0
      );
      return newSelected;
    });
  };

  const renderLeadsTable = () => (
    <table className="table table-hover align-middle">
      <thead className="thead-light">
        <tr>
          <th>{getLifecycleTypeName("LEAD")} Title</th>
          <th>Company</th>
          <th>Estimated Value</th>
          <th>Contact Number</th>
          <th>{getLifecycleTypeName("LEAD")} Status</th>
        </tr>
      </thead>
      <tbody>
        {(currentData as Lead[]).map((lead) => (
          <tr key={lead.lead_id}>
            <td>
              <h2 className="d-flex align-items-center" style={{ cursor: "pointer" }}>
                <Link
                  to={`/leads/${lead.lead_id}`}
                  target="_blank"
                  state={{ leadData: lead }}
                  className="d-flex flex-column"
                >
                  {lead.title && lead.title.length > 30 ? (
                    <Tooltip title={lead.title} placement="top" arrow>
                      <p className="mb-1" style={{ cursor: "pointer" }}>
                        {`${lead.title.substring(0, 30)}...`}
                      </p>
                    </Tooltip>
                  ) : (
                    <p className="mb-1" style={{ cursor: "pointer" }}>{lead.title || "-"}</p>
                  )}
                  <span className="text-default">{lead.lifecycle_source?.lifecycle_source_display_name || lead.source}</span>
                </Link>
              </h2>
            </td>
            <td>
              <div>{lead.company?.company_name || "No Company"}</div>
            </td>
            <td>
              <div>
                {lead.estimated_value
                  ? `${lead.currency_code || "$"
                  } ${lead.estimated_value.toFixed(4)}`
                  : "Not specified"}
              </div>
            </td>
            <td>
              <div>
                {lead.primary_contact?.primary_phone || "No Contact Number"}
              </div>
            </td>
            <td>
              <span
                className={`badge badge-pill badge-status ${getStageColor(
                  lead.lifecycle_type_stage?.stage_name || "",
                  lead.lifecycle_type_stage?.lifecycle_type_stage_id,
                  lead.lifecycle_type_stage?.stage_order
                )} no-border`}
                style={{
                  border: "none",
                  borderColor: "transparent",
                }}
              >
                {lead.lifecycle_type_stage?.stage_name || "No Status"}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderPipelinesTable = () => (
    <table className="table table-hover align-middle">
      <thead className="thead-light">
        <tr>
          <th>{getLifecycleTypeName("PIPELINE")} Title</th>
          <th>Priority</th>
          <th>Contact Number</th>
          <th>{getLifecycleTypeName("PIPELINE")} Status</th>
        </tr>
      </thead>
      <tbody>
        {(currentData as Pipeline[]).map((pipeline) => (
          <tr key={pipeline.pipeline_id}>
            <td>
              <h2 className="d-flex align-items-center">
                <Link
                  to={`/pipelines/${pipeline.pipeline_id}`}
                  target="_blank"
                  state={{ pipelineData: pipeline }}
                  className="d-flex flex-column"
                >
                  {pipeline.title && pipeline.title.length > 30 ? (
                    <Tooltip title={pipeline.title} placement="top" arrow>
                      <p className="mb-1" style={{ cursor: "pointer" }}>
                        {`${pipeline.title.substring(0, 30)}...`}
                      </p>
                    </Tooltip>
                  ) : (
                    <p className="mb-1" style={{ cursor: "pointer" }}>{pipeline.title || "-"}</p>
                  )}
                  <span className="text-muted small">
                    {pipeline.company?.company_name || "No Company"}
                  </span>
                </Link>
              </h2>
            </td>
            <td>
              <span className="text-default">
                {getPriorityIcon(pipeline.priority)}
                {pipeline.priority || "No Priority"}
              </span>
            </td>
            <td>
              <div>
                {pipeline.primary_contact?.primary_phone || "No Contact Number"}
              </div>
            </td>
            <td>
              <span
                className={`badge badge-pill badge-status ${getStageColor(
                  pipeline.lifecycle_type_stage?.stage_name || "",
                  pipeline.lifecycle_type_stage?.lifecycle_type_stage_id,
                  pipeline.lifecycle_type_stage?.stage_order
                )} no-border`}
                style={{
                  border: "none",
                  borderColor: "transparent",
                }}
              >
                {pipeline.lifecycle_type_stage?.stage_name || "No Status"}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderDealsTable = () => (
    <table className="table table-hover align-middle">
      <thead className="thead-light">
        <tr>
          <th>{getLifecycleTypeName("DEAL")} Title</th>
          <th>Priority</th>
          <th>Estimated Value</th>
          <th>Contact Number</th>
          <th>{getLifecycleTypeName("DEAL")} Status</th>
        </tr>
      </thead>
      <tbody>
        {(currentData as Deal[]).map((deal) => (
          <tr key={deal.deal_id}>
            <td>
              <div>
                <div>
                  <Link
                    to={`/deals/${deal.deal_id}`}
                    target="_blank"
                    state={{ dealData: deal }}
                    className="title-name"
                  >
                    {deal.title && deal.title.length > 30 ? (
                      <Tooltip title={deal.title} placement="top" arrow>
                        <p className="mb-1" style={{ cursor: "pointer" }}>
                          {`${deal.title.substring(0, 30)}...`}
                        </p>
                      </Tooltip>
                    ) : (
                      <p className="mb-1" style={{ cursor: "pointer" }}>{deal.title || "-"}</p>
                    )}
                  </Link>
                </div>
                <span className="text-muted small">
                  {deal.company?.company_name || "No Company"}
                </span>
              </div>
            </td>
            <td>
              {deal.priority && (
                <div className="text-default">
                  {getPriorityIcon(deal.priority)}
                  {deal.priority}
                </div>
              )}
              {!deal.priority && (
                <span className="text-muted">No Priority</span>
              )}
            </td>
            <td>
              {deal.estimated_value
                ? `${deal.currency_code || "$"} ${parseFloat(
                  deal.estimated_value.toFixed(4)
                ).toLocaleString()}`
                : "-"}
            </td>
            <td>{deal.primary_contact?.primary_phone || "-"}</td>
            <td>
              <span
                className={`badge badge-pill badge-status ${getStageColor(
                  deal.lifecycle_type_stage?.stage_name || "",
                  deal.lifecycle_type_stage?.lifecycle_type_stage_id,
                  deal.lifecycle_type_stage?.stage_order
                )} no-border`}
                style={{
                  border: "none",
                  borderColor: "transparent",
                }}
              >
                {deal.lifecycle_type_stage?.stage_name || "Not Set"}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderCurrentTable = () => {
    switch (selectedStageType.value) {
      case "lead":
        return renderLeadsTable();
      case "pipeline":
        return renderPipelinesTable();
      case "deal":
        return renderDealsTable();
      default:
        return null;
    }
  };

  return (
    <div className="tab-pane fade" id="stages">
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <div className="form-sort me-3 mt-0">
            <Select
              className="select"
              options={stageTypeOptions}
              value={selectedStageType ?? ""}
              onChange={(selectedOption) => {
                if (selectedOption) {
                  setSelectedStageType(selectedOption);
                }
              }}
              placeholder="Select Stage Type"
              classNamePrefix="react-select"
            />
          </div>
          <div className="d-inline-flex align-items-center">
            <button
              className="btn btn-primary btn-sm"
              onClick={handleAddNew}
              disabled={creatingEntity}
            >
              {creatingEntity ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Creating...
                </>
              ) : (
                <>
                  <i className="fa fa-plus me-1" />
                  Add New {selectedStageType.label}
                </>
              )}
            </button>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading data...</p>
            </div>
          ) : error ? (
            <div className="text-center p-4">
              <div className="alert alert-danger" role="alert">
                <i className="ti ti-alert-circle me-2"></i>
                {error}
              </div>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={loadStageData}
              >
                <i className="ti ti-refresh me-1"></i>
                Retry
              </button>
            </div>
          ) : (
            <div className="stages-content">
              {allData.length === 0 ? (
                <div className="text-center p-4">
                  <div className="mb-3">
                    <i className="ti ti-activity fs-48 text-muted"></i>
                  </div>
                  <h6 className="text-muted">
                    No {selectedStageType.label.toLowerCase()} found
                  </h6>
                  <p className="text-muted mb-3">
                    No {selectedStageType.label.toLowerCase()} records are
                    associated with this contact.
                  </p>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleAddNew}
                    disabled={creatingEntity}
                  >
                    {creatingEntity ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-plus me-1" />
                        Create {selectedStageType.label}
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <>
                  <div className="table-responsive">{renderCurrentTable()}</div>
                  {totalPages > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <p className="mb-0 text-muted">
                        Showing {indexOfFirst + 1}–
                        {Math.min(indexOfLast, allData.length)} of{" "}
                        {allData.length}{" "}
                        {getLifecycleTypeName(selectedStageType.label)}
                      </p>
                      {totalPages > 1 && (
                        <nav>
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""
                                }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  setCurrentPage((p) => Math.max(1, p - 1))
                                }
                              >
                                Prev
                              </button>
                            </li>

                            {getPageNumbers().map((page) => (
                              <li
                                key={page}
                                className={`page-item ${currentPage === page ? "active" : ""
                                  }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            ))}

                            <li
                              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                                }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  setCurrentPage((p) =>
                                    Math.min(totalPages, p + 1)
                                  )
                                }
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Stages;
